import { useState, useEffect, useCallback } from 'react';
import { Star, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { GOOGLE_REVIEW_URL } from '@/utils/googleReviewQrData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const [rList, rStats] = await Promise.all([
        axios.get(`${API}/reviews`),
        axios.get(`${API}/reviews/stats`),
      ]);
      setReviews(rList.data || []);
      setStats(rStats.data || { average: 0, count: 0 });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Inject AggregateRating Schema.org JSON-LD once stats are loaded
  useEffect(() => {
    if (stats.count === 0) return;
    const scriptId = 'taj-aggregate-rating';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      '@id': 'https://www.tajmahalsanfernando.es/#restaurant',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: stats.average,
        reviewCount: stats.count,
        bestRating: 5,
        worstRating: 1,
      },
    });
    document.head.appendChild(script);
  }, [stats]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Introduce tu nombre'); return; }
    if (rating < 1 || rating > 5) { toast.error('Elige entre 1 y 5 estrellas'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        name, rating, comment, honeypot, order_id: '',
      });
      toast.success(data.message || 'Reseña enviada');
      setName(''); setComment(''); setRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (n, interactive = false) => Array.from({ length: 5 }, (_, i) => {
    const val = i + 1;
    const filled = interactive ? val <= (hoverRating || rating) : val <= n;
    return (
      <Star
        key={i}
        className={`w-5 h-5 ${filled ? 'fill-[#E28743] text-[#E28743]' : 'text-[#E8DEC8]'} ${interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''}`}
        onClick={interactive ? () => setRating(val) : undefined}
        onMouseEnter={interactive ? () => setHoverRating(val) : undefined}
        onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
        data-testid={interactive ? `star-${val}` : undefined}
      />
    );
  });

  return (
    <section id="resenas" className="section-padding bg-[#FDFBF7]" data-testid="reviews-section">
      <div className="container-max">
        <div className="text-center mb-12">
          <span className="overline mb-4 block">Opiniones</span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#2D231E] mb-4">
            Lo Que Dicen Nuestros Clientes
          </h2>
          {stats.count > 0 ? (
            <div className="flex items-center justify-center gap-3" data-testid="reviews-aggregate">
              <div className="flex">{renderStars(Math.round(stats.average))}</div>
              <span className="font-sans text-lg text-[#2D231E]">
                <strong>{stats.average.toFixed(1)}</strong>
                <span className="text-[#5C4D43]"> / 5 · {stats.count} reseña{stats.count === 1 ? '' : 's'}</span>
              </span>
            </div>
          ) : (
            <p className="font-sans text-[#5C4D43]">Se el primero en valorarnos.</p>
          )}
        </div>

        {/* Approved reviews grid */}
        {!loading && reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="reviews-list">
            {reviews.map((r) => (
              <article key={r.id} className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]" data-testid={`review-${r.id}`}>
                <div className="flex mb-2">{renderStars(r.rating)}</div>
                {r.comment && <p className="font-sans text-[#5C4D43] italic mb-3 leading-relaxed">&ldquo;{r.comment}&rdquo;</p>}
                <p className="font-sans text-sm text-[#2D231E] font-semibold">{r.name}</p>
              </article>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Submit review form */}
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-[#E8DEC8] space-y-4" data-testid="review-form">
            <h3 className="font-serif text-xl font-semibold text-[#2D231E]">Deja tu opinion</h3>
            <div>
              <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Tu nombre *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Juan Perez"
                className="bg-[#FDFBF7] border-[#E8DEC8] focus:ring-[#C44536]"
                maxLength={80}
                data-testid="review-name-input"
              />
            </div>
            <div>
              <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Valoracion *</label>
              <div className="flex gap-1" role="radiogroup" aria-label="Valoracion en estrellas">
                {renderStars(rating, true)}
              </div>
            </div>
            <div>
              <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Comentario (opcional)</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Cuentanos tu experiencia..."
                rows={4}
                maxLength={1000}
                className="bg-[#FDFBF7] border-[#E8DEC8] focus:ring-[#C44536]"
                data-testid="review-comment-input"
              />
            </div>
            {/* Honeypot: hidden from users, bots fill it */}
            <input
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
              aria-hidden="true"
            />
            <Button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="review-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar reseña
            </Button>
            <p className="text-xs text-[#8A7A6D] font-sans">Tu reseña se publicara tras una breve revision para evitar spam.</p>
          </form>

          {/* Google review CTA */}
          <div className="bg-gradient-to-br from-[#C44536] to-[#9B2F24] rounded-2xl p-6 text-white flex flex-col justify-center text-center md:text-left" data-testid="google-review-cta">
            <div className="flex justify-center md:justify-start mb-3">
              {Array.from({ length: 5 }, (_, i) => <Star key={i} className="w-6 h-6 fill-[#FFD166] text-[#FFD166]" />)}
            </div>
            <h3 className="font-serif text-2xl font-bold mb-2">Ya eres cliente?</h3>
            <p className="font-sans text-sm mb-5 opacity-90">
              Valoranos tambien en <strong>Google</strong>. Ayuda a otros a descubrirnos y nos motiva a seguir mejorando.
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#C44536] font-semibold px-6 py-3 rounded-full hover:bg-[#FDFBF7] transition-colors shadow-lg"
              data-testid="google-review-link"
            >
              <Star className="w-4 h-4 fill-[#E28743] text-[#E28743]" />
              Valorar en Google
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
